<template>
  <div class="devApiDocs">
    <h1>API Documentation</h1>
    <br />
    <br />
    <div class="documentationSection">
      <h2>Kiuki API</h2>
      <div class="content">
        The Kiuki.com API is a RESTful API that helps you add grocery shopping
        features to your applications.
        <br />
        <b>With the Kiuki.com API, you can:</b>
        <ul>
          <li>✅ List products dynamically.</li>
          <li>✅ Create grocery shopping orders</li>
        </ul>
      </div>
    </div>
    <br />
    <div class="documentationSection">
      <h2>Base URL</h2>
      <div class="content">
        The Kiuki.com API follows a RESTful structure with the following base
        URL: https://api.kiuki.com/v1/ All API requests must be sent via
        <b>HTTPS</b> to ensure security.
      </div>
    </div>
    <br />
    <div class="documentationSection">
      <h3>Authentication & Security</h3>
      <div class="content">
        Kiuki.com API uses <b>API Key-based authentication</b> to ensure
        secure access to the platform. Each request must include
        authentication headers to validate the partner's identity.
      </div>
    </div>
    <br />
    <div class="documentationSection">
      <h3>1. Obtaining API Credentials</h3>
      <div class="content">
        To authenticate API requests, you must obtain your API Key and API
        Secret from your Kiuki.com account.
        <ul>
          <li>Log in to Kiuki.com</li>
          <li>Navigate to API Credentials.</li>
          <li>Generate your API Key and API Secret.</li>
        </ul>
      </div>
    </div>
    <br />
    <div class="documentationSection">
      <h3>2. API Authentication Headers</h3>
      <div class="content">
        Each API request must include the following HTTP headers:
        <br />
        <pre>
          <code>
X-Auth-Method: api-key
X-Api-Key: YOUR_API_KEY
X-Api-Secret: YOUR_API_SECRET</code>
        </pre>
      </div>
    </div>
    <br />
    <div class="documentationSection">
      <h3>3. Example API Request</h3>
      <div class="content">
        <b>cURL Example</b>
        <br />
        <pre>
          <code>
curl -X GET "https://api.kiuki.com/v1/products" \
     -H "X-Auth-Method: api-key" \
     -H "X-Api-Key: YOUR_API_KEY" \</code>
        </pre>
      </div>
    </div>
    <br />
    <div class="documentationSection">
      <h3>4. Security Best Practices</h3>
      <div class="content">
        <ul>
          <li>Keep your API Key and API Secret confidential.</li>
          <li>Use HTTPS for all API requests.</li>
          <li>Implement rate limiting to prevent abuse.</li>
          <li>Rotate API keys periodically for security.</li>
        </ul>
      </div>
    </div>
    <br />
    <div class="documentationSection">
      <h2>API Endpoints</h2>
      <div class="content">
        <h3>Product Management</h3>
        <h4>Get Product List</h4>
        <code><b>GET</b> /products</code>
        <br />
        <h4>Get Product details by ID</h4>
        <code><b>GET</b> /products/{product_id}</code>
        <br />
        <h3>Orders & checkout</h3>
        <h4>Place Order</h4>
        <code><b>POST</b> /orders</code>
        <h4>Request Body:</h4>
        <pre>
          <code>
{
  "address": {
    "description": "Demo001",
    "street_address": "70 Ramsgate Rd",
    "city": "Wilsford",
    "postcode": "NG32 3AH",
    "country": "GB",
    "region_postal": "",
    "house_number": "70",
    "is_default": true,
    "recipient": {
      "firstname": "Nayan",
      "lastname": "M",
      "company": "",
      "contact_email": "nayan9229@gmail.com",
      "contact_phone": "+447007129612"
    }
  },
  "delivery_date": "2025-02-07",
  "items": [
  {
    "item_id": "pof_Wrvx7vXnPzEGfRxW",
    "quantity": 2
  },
  {
    "item_id": "pof_7YAvyTNOxsRwZObr",
    "quantity": 1
    }
  ]
}
          </code>
        </pre>
        <!--  -->
        <!--  -->
        <h4>Root Object</h4>
        <div class="tableWrapper">
          <table cellpadding="15">
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
              <th>Required</th>
            </tr>
            <tr>
              <td>address</td>
              <td>object</td>
              <td>Customer address details</td>
              <td>Yes</td>
            </tr>
            <tr>
              <td>delivery_date</td>
              <td>object</td>
              <td>Delivery date per organization ID</td>
              <td>Yes</td>
            </tr>
            <tr>
              <td>items</td>
              <td>array</td>
              <td>List of items to order</td>
              <td>Tes</td>
            </tr>
          </table>
        </div>
        <!--  -->
        <!--  -->
        <h4>Address Object</h4>
        <div class="tableWrapper">
          <table cellpadding="15">
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
              <th>Required</th>
            </tr>
            <tr>
              <td>description</td>
              <td>string</td>
              <td>Address nickname or description</td>
              <td>No</td>
            </tr>
            <tr>
              <td>street_address</td>
              <td>string</td>
              <td>Street address</td>
              <td>Yes</td>
            </tr>
            <tr>
              <td>city</td>
              <td>string</td>
              <td>City Name</td>
              <td>Yes</td>
            </tr>
            <tr>
              <td>postcode</td>
              <td>string</td>
              <td>Postal code</td>
              <td>Yes</td>
            </tr>
            <tr>
              <td>country</td>
              <td>string</td>
              <td>Country code (ISO 3166-1 alpha-2)</td>
              <td>Yes</td>
            </tr>
            <tr>
              <td>region_postal</td>
              <td>string</td>
              <td>Regional postal code (if any</td>
              <td>No</td>
            </tr>
            <tr>
              <td>house_number</td>
              <td>string</td>
              <td>House number</td>
              <td>No</td>
            </tr>
            <tr>
              <td>is_default</td>
              <td>boolean</td>
              <td>Whether this is the default address</td>
              <td>No</td>
            </tr>
            <tr>
              <td>recipient</td>
              <td>object</td>
              <td>Recipient details</td>
              <td>Yes</td>
            </tr>
          </table>
        </div>
        <!--  -->
        <!--  -->
        <h4>Recipient Object</h4>
        <div class="tableWrapper">
          <table cellpadding="15">
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
              <th>Required</th>
            </tr>
            <tr>
              <td>firstname</td>
              <td>string</td>
              <td>Recipient's first name</td>
              <td>Yes</td>
            </tr>
            <tr>
              <td>lastname</td>
              <td>string</td>
              <td>Recipient's last name</td>
              <td>Yes</td>
            </tr>
            <tr>
              <td>company</td>
              <td>string</td>
              <td>Company name (if applicable)</td>
              <td>No</td>
            </tr>
            <tr>
              <td>contact_email</td>
              <td>string</td>
              <td>Contact email</td>
              <td>Yes</td>
            </tr>
            <tr>
              <td>contact_phone</td>
              <td>string</td>
              <td>Contact phone number</td>
              <td>Yes</td>
            </tr>
          </table>
        </div>
        <!--  -->
        <!--  -->
        <h4>Items Array</h4>
        <div class="tableWrapper">
          <table cellpadding="15">
            <tr>
              <th>Parameter</th>
              <th>Type</th>
              <th>Description</th>
              <th>Required</th>
            </tr>
            <tr>
              <td>item_id</td>
              <td>string</td>
              <td>Unique offering item identifier</td>
              <td>Yes</td>
            </tr>
            <tr>
              <td>quantity</td>
              <td>integer</td>
              <td>Quantity of the item</td>
              <td>Yes</td>
            </tr>
          </table>
        </div>
        <br />
        <h4>Get Placed Order by ID</h4>
        <code><b>GET</b> /orders/{order_id}</code>
      </div>
    </div>
    <br />
    <br />
    <br />
    <div class="documentationSection">
      <h2>Notes</h2>
      <div class="content">
        <i>
          Before using the Kiuki.com API, users must register on Kiuki.com
          and complete the necessary setup. This includes:
          <ul>
            <li>Creating an account on Kiuki.com.</li>
            <li>
              Setting up an address and preferred payment method in their profile.
            </li>
            <li>
              Generating API credentials – Once the setup is complete, users can
              obtain their API Key and API Secret from Kiuki.com.
            </li>
            <li>
              To Generate the API Keys, open the Sidebar by clicking on your account
              picture on the top right of the screen, and then select <b>API Keys Settings</b>.
              On there API Key and Secret can be generated or Refreshed, and Credits can be bought
            </li>
          </ul>
        </i>
      </div>
    </div>
    <br />
  </div>
</template>

<script>
export default {
  name: 'DevApi',
  metaInfo () {
    return {
      title: 'Development Docs'
    }
  },
  data: function () {
    return {
      algoliaIndex: process.env.VUE_APP_ALGOLIA_INDEX
    }
  }
  // computed: {
  //   userData () {
  //     return this.$store.state.userData
  //   },
  //   veganUser () {
  //     if (this.userData.attrs !== undefined &&
  //       this.userData.attrs !== null &&
  //       this.userData.attrs.vegan_only !== null &&
  //       this.userData.attrs.vegan_only !== undefined) {
  //       return this.userData.attrs.vegan_only
  //     }
  //     // By Default, user is not considered "Vegan"
  //     return false
  //   },
  //   showVeganItemsOnly () {
  //     // Return true if we want to show only vegan items
  //     if (this.veganUser) return true
  //     // ? We probably don't need this ignoreVeganFlag as it's a
  //     // General marketplace now
  //     if (this.ignoreVeganFlag) return false
  //     // By Default show all items
  //     return false
  //   }
  // }
}
</script>

<style scoped lang="stylus">
.devApiDocs {
  display flex
  background white
  align-items flex-start
  text-align left
  justify-content center
  height 100%
  flex-wrap wrap
  padding 50px 10px
  box-sizing border-box

  margin 0 auto
  text-align left
  max-width 900px
  p {
    text-align left
  }
  .documentationSection {
    display: flex;
    flex-direction: column;
    width: 100%;

    h1, h2 {
      text-align left
    }
    h2 {
      margin: .75em 0;
    }
    h1 {
      font-family: 'Bebas Neue', sans-serif;
    }
    h4 {
      font-size: 16px;
    }
    h2, h3, h4 {
      font-family: Helvetica
    }

    .content {
      code, pre, .tableWrapper {
        overflow: auto;
      }

      table {
        tr {
          td, th {
            padding: 0 15px;
          }
        }
      }
    }
  }

  .pl-1 {
    padding-left: 32px;
  }
}
</style>
